import type { EventStoryblok } from '@/types'

// these adapters are responsible for generating json-ld structured data based on the content of the page story
// date format: 2024-02-28T08:00 (YYYY-MM-DDTHH:MM) (no hour if all day event)

enum SchemaMarkupType {
  Event = 'Event',
  Article = 'Article',
  BreadcrumbList = 'BreadcrumbList',
  FAQPage = 'FAQPage',
  Organization = 'Organization',
}

enum EventStatus {
  Scheduled = 'EventScheduled',
  Cancelled = 'EventCancelled',
}

enum EventAttandanceMode {
  Online = 'OnlineEventAttendanceMode',
  Offline = 'OfflineEventAttendanceMode',
}

interface Address {
  '@type': 'PostalAddress'
  streetAddress: string
  addressLocality: string
  postalCode: string
  addressCountry: string
}

interface LocationOffline {
  '@type': 'Place'
  name: string
  address: Address
}

interface LocationOnline {
  '@type': 'VirtualLocation'
  url: string
}

const parseDate = (date: string) => {
  if (!date) return ''
  const dateArray = date.split(' ')

  if (dateArray.length === 1) return dateArray[0]

  const datePart = dateArray[0]
  const timePart = dateArray[1] || '00:00'
  return `${datePart}T${timePart}`
}

export interface EventLd {
  '@context': 'https://schema.org'
  '@type': SchemaMarkupType.Event
  name: string
  description: string
  image?: string
  startDate: string
  endDate: string
  eventStatus: `https://schema.org/${EventStatus}`
  eventAttendanceMode: `https://schema.org/${EventAttandanceMode}`
  location: LocationOffline | LocationOnline
  performer: {
    '@type': 'Organization'
    name: 'Infermedica'
  }
}

export const eventStructuredDataAdapter = (event: EventStoryblok) => {
  const location: LocationOffline | LocationOnline =
    event.content.attendance_mode === EventAttandanceMode.Offline
      ? {
          '@type': 'Place',
          name: event.content.venue_name || '',
          address: {
            '@type': 'PostalAddress',
            streetAddress: event.content.street || '',
            addressLocality: event.content.city || '',
            postalCode: event.content.zip_code || '',
            addressCountry: event.content.country || '',
          },
        }
      : {
          '@type': 'VirtualLocation',
          url: event.url || '',
        }

  const eventLd: EventLd = {
    '@context': 'https://schema.org',
    '@type': SchemaMarkupType.Event,
    name: event.content.meta?.title || '',
    description: event.content.meta?.description || '',
    startDate: parseDate(event.content.start_date) || '',
    endDate: parseDate(event.content.end_date) || '',
    eventStatus: `https://schema.org/${EventStatus.Scheduled}`,
    eventAttendanceMode: `https://schema.org/${
      (event.content.attendance_mode as EventAttandanceMode) || ''
    }`,
    location,
    performer: {
      '@type': 'Organization',
      name: 'Infermedica',
    },
  }

  Object.keys(eventLd).forEach(
    (key) =>
      eventLd[key as keyof EventLd] === '' &&
      delete eventLd[key as keyof EventLd],
  )

  return eventLd
}
